/**
 * One-Click Checkout System
 *
 * Provides "Buy Now" functionality that:
 * - Skips cart and goes directly to checkout
 * - Optimized for returning customers
 * - Tracks conversion events
 * - Mobile-optimized user experience
 */

class OneClickCheckout {
  constructor() {
    this.buttons = document.querySelectorAll('.buy-now-button');
    this.init();
  }

  init() {
    if (this.buttons.length === 0) return;

    this.buttons.forEach(button => {
      button.addEventListener('click', this.handleBuyNow.bind(this));
    });

    // Listen for variant changes on product pages
    document.addEventListener('variant:changed', this.handleVariantChange.bind(this));
  }

  async handleBuyNow(event) {
    const button = event.currentTarget;

    // Prevent double clicks
    if (button.disabled || button.classList.contains('loading')) {
      return;
    }

    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const productTitle = button.dataset.productTitle;
    const variantPrice = button.dataset.variantPrice;

    if (!variantId) {
      console.error('Buy Now: Missing variant ID');
      return;
    }

    // Set loading state
    this.setLoadingState(button, true);

    try {
      // Add to cart via Shopify API
      const response = await this.addToCart(variantId);

      if (response.ok) {
        // Track the event
        this.trackBuyNowClick(productId, productTitle, variantPrice);

        // Redirect to checkout
        window.location.href = '/checkout';
      } else {
        const errorData = await response.json();
        this.handleError(button, errorData);
      }
    } catch (error) {
      console.error('Buy Now error:', error);
      this.handleError(button, { message: 'An error occurred. Please try again.' });
    } finally {
      this.setLoadingState(button, false);
    }
  }

  async addToCart(variantId, quantity = 1) {
    const formData = {
      items: [
        {
          id: variantId,
          quantity: quantity
        }
      ]
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(formData)
    });
  }

  setLoadingState(button, isLoading) {
    if (isLoading) {
      button.classList.add('loading');
      button.disabled = true;
      const spinner = button.querySelector('.loading-spinner');
      if (spinner) spinner.hidden = false;
    } else {
      button.classList.remove('loading');
      button.disabled = false;
      const spinner = button.querySelector('.loading-spinner');
      if (spinner) spinner.hidden = true;
    }
  }

  handleError(button, errorData) {
    // Show error message
    const errorMessage = errorData.message || errorData.description || 'Unable to process request';

    // Create temporary error display
    const errorDiv = document.createElement('div');
    errorDiv.className = 'buy-now-error';
    errorDiv.textContent = errorMessage;
    errorDiv.style.cssText = `
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 8px;
      padding: 8px 12px;
      background: #dc2626;
      color: white;
      border-radius: 4px;
      font-size: 13px;
      z-index: 10;
      animation: slideDown 0.2s ease;
    `;

    // Insert error message
    const wrapper = button.closest('.buy-now-wrapper');
    if (wrapper) {
      const existingError = wrapper.querySelector('.buy-now-error');
      if (existingError) existingError.remove();

      wrapper.style.position = 'relative';
      wrapper.appendChild(errorDiv);

      // Remove after 5 seconds
      setTimeout(() => {
        errorDiv.style.animation = 'slideUp 0.2s ease';
        setTimeout(() => errorDiv.remove(), 200);
      }, 5000);
    }
  }

  handleVariantChange(event) {
    const { variant } = event.detail;

    // Update all Buy Now buttons with new variant
    this.buttons.forEach(button => {
      if (variant) {
        button.dataset.variantId = variant.id;
        button.dataset.variantPrice = variant.price;
        button.disabled = !variant.available;

        const buttonText = button.querySelector('.buy-now-text');
        if (buttonText) {
          buttonText.textContent = variant.available
            ? (button.dataset.buyNowText || 'Buy Now')
            : (button.dataset.soldOutText || 'Sold Out');
        }
      }
    });
  }

  trackBuyNowClick(productId, productTitle, variantPrice) {
    // Track with Google Analytics 4 if available
    if (typeof gtag === 'function') {
      gtag('event', 'buy_now_click', {
        event_category: 'ecommerce',
        event_label: productTitle,
        value: parseFloat(variantPrice) / 100,
        product_id: productId
      });
    }

    // Track with Facebook Pixel if available
    if (typeof fbq === 'function') {
      fbq('track', 'InitiateCheckout', {
        content_name: productTitle,
        content_ids: [productId],
        content_type: 'product',
        value: parseFloat(variantPrice) / 100,
        currency: window.Shopify?.currency?.active || 'USD'
      });
    }

    // Publish internal event for other tracking systems
    if (typeof PubSub !== 'undefined' && PubSub.publish) {
      PubSub.publish('buy-now:click', {
        productId,
        productTitle,
        variantPrice
      });
    }
  }
}

// Add slideDown/slideUp animations
if (!document.getElementById('buy-now-animations')) {
  const style = document.createElement('style');
  style.id = 'buy-now-animations';
  style.textContent = `
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes slideUp {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(-10px);
      }
    }
  `;
  document.head.appendChild(style);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new OneClickCheckout();
  });
} else {
  new OneClickCheckout();
}

// Re-initialize when theme editor updates sections
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new OneClickCheckout();
  });
}
